/**
 * Bundle PDP behaviour.
 *
 * Loaded from layout/theme.liquid on the product.bundle template only, after
 * global.min.js, so Delegate and RevolutionSwiper are already defined.
 */

class BundleSelector extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.delegate = new Delegate(this.element);
  }

  connectedCallback() {
    this.toggleButton = this.element.querySelector(
      '[data-action="toggle-bundle-selector"]'
    );
    this.content = this.element.querySelector(".bundle-selector__content");
    this.slider = this.element.querySelector(".bundle-selector__slider");
    this.track = this.element.querySelector(".bundle-selector__track");

    this.bindEvents();
    this.buildSlider();
  }

  disconnectedCallback() {
    this.delegate.destroy();

    // Only tear down a slider this element started. The one ProductElement
    // creates belongs to ProductElement.
    if (this.swiper && this.ownsSwiper) {
      this.swiper.destroy();
    }
  }

  /**
   * Binds every event this element listens to.
   *
   * The accordion trigger is a native <button>, which already turns Enter into a
   * click, so a keypress listener on it would toggle the panel twice per press.
   * The bundle cards are anchors and behave the same way.
   */
  bindEvents() {
    this.delegate.on(
      "click",
      '[data-action="toggle-bundle-selector"]',
      this.toggleContent.bind(this)
    );
  }

  /**
   * Opens or closes the step's content panel and keeps the trigger's
   * aria-expanded in sync with it.
   */
  toggleContent() {
    const isOpen = this.toggleButton.getAttribute("aria-expanded") === "true";
    const willOpen = !isOpen;

    this.toggleButton.setAttribute("aria-expanded", willOpen);
    this.content.setAttribute("aria-hidden", !willOpen);

    if (willOpen && this.swiper) {
      this.swiper.update();
      this.updateOverlays();
    }
  }

  /**
   * Attaches to the slider and brings the selected bundle fully into view, which
   * matters when it sits further along than the first screenful of cards.
   *
   * ProductElement initialises every '.swiper' inside '.pm__information' from
   * its data-swiper-settings, and on a normal page load it runs first — both
   * scripts are deferred and global.min.js comes before this one. Starting a
   * second Swiper on the same track would leave two instances fighting over its
   * offsets and transforms, so adopt that one instead. After a soft product
   * swap ProductElement does not run again, and this element builds its own.
   */
  buildSlider() {
    if (!this.track) return;

    this.ownsSwiper = !this.track.swiper;

    if (this.ownsSwiper) {
      this.carousel = new RevolutionSwiper(this.track);
      this.swiper = this.carousel.swiperElement;
    } else {
      this.swiper = this.track.swiper;
    }

    this.boundUpdateOverlays = this.updateOverlays.bind(this);

    this.swiper.on("progress", this.boundUpdateOverlays);
    this.swiper.on("resize", this.boundUpdateOverlays);

    this.scrollSelectedIntoView();
    this.updateOverlays();
  }

  /**
   * Moves the slider to the card of the bundle the PDP is currently showing.
   */
  scrollSelectedIntoView() {
    const selectedCard = this.element.querySelector(
      ".bundle-selector__card--selected"
    );

    if (!selectedCard) return;

    const selectedSlide = selectedCard.closest(".bundle-selector__slide");
    const slides = Array.from(selectedSlide.parentNode.children);
    const selectedIndex = slides.indexOf(selectedSlide);
    const withoutAnimation = 0;

    this.swiper.slideTo(selectedIndex, withoutAnimation);
  }

  /**
   * Shows the edge fade only on a side that still has cards to scroll to.
   */
  updateOverlays() {
    this.slider.setAttribute("data-can-scroll-left", !this.swiper.isBeginning);
    this.slider.setAttribute("data-can-scroll-right", !this.swiper.isEnd);
  }
}

customElements.define("bundle-selector", BundleSelector);


/**
 * Bundle PDP — step 2, the colour pickers.
 *
 * The Simple Bundles app block is the source of truth for what can be chosen: it
 * renders one <select name="properties[Product N]"> per configurable component.
 * The pickers themselves are rendered in Liquid, one per component in the same
 * order; this element hides the app block, binds each picker to its select, and
 * writes every pick back.
 *
 * The app block sits outside the product form on this theme, so its selects are
 * never serialised by AddToCart. Each pick is therefore mirrored into a hidden
 * input inside the form as well — that is what actually reaches /cart/add.js.
 */

const APP_OPTIONS_SELECTOR = "#simple-bundles-io-options";
const MISSING_LABEL_DURATION = 2500;
const BUY_BUTTONS_SELECTOR = ".pm__buttons-pay-quantity";

class BundleColors extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.delegate = new Delegate(this.element);
    this.pickers = [];
    this.orphanSelects = [];
  }

  connectedCallback() {
    this.appOptions = document.querySelector(APP_OPTIONS_SELECTOR);
    this.productForm = document.querySelector("[data-product-form]");
    this.stepToggle = this.element.querySelector(
      '[data-action="toggle-bundle-colors"]'
    );
    this.stepContent = this.element.querySelector(".bundle-colors__content");

    // Without the app block there is nothing to choose between, and without the
    // form there is nowhere to put the answer. Either way, stay out of the page.
    if (!this.appOptions || !this.productForm) {
      this.showEditorNotice();
      return;
    }

    this.bindPickersToSelects();
    this.bindEvents();
    this.openFirstUnpicked();

    // Swatches the app does not offer were removed after the sliders were built,
    // so the slide count they measured is stale.
    this.pickers.forEach((picker) => picker.swiper && picker.swiper.update());

    // Tell step 3 which components are actually in play, so it can drop rows for
    // any the app turned out not to offer.
    this.announceSelections();
  }

  disconnectedCallback() {
    this.delegate.destroy();
    document.body.removeEventListener("click", this.addToCartGuard, true);
  }

  /**
   * A step that cannot render leaves nothing behind on the storefront. In the
   * theme editor it says what it is waiting for instead, since a step that
   * silently vanishes there reads as a broken block.
   */
  showEditorNotice() {
    const notice = this.element.querySelector('[data-role="editor-notice"]');

    if (!notice) {
      this.element.setAttribute("hidden", "hidden");
      return;
    }

    notice.hidden = false;
    this.element
      .querySelectorAll('[data-role="step"]')
      .forEach((part) => (part.hidden = true));
  }


  /**
   * Pairs each Liquid-rendered picker with the app select that offers its
   * colours and gives it a hidden input inside the product form.
   *
   * Liquid renders a picker per product in the bundle's components metafield,
   * while the app renders a select per colour group, so there are always more
   * pickers than selects and the two lists are in whatever order the merchant
   * left the metafield in. The pairing is therefore made on the product titles
   * the two sides share rather than on position, and pickers are walked in
   * document order so the rows a customer sees stay in metafield order.
   *
   * A picker with no matching select is a duplicate of a group already drawn,
   * and a swatch whose value is not among that select's options is a colour the
   * app will not sell — both are taken off the page rather than shown as
   * choices that cannot be made.
   */
  bindPickersToSelects() {
    const selects = Array.from(this.appOptions.querySelectorAll("select"));
    const unclaimed = new Set(selects);
    const pickerElements = this.element.querySelectorAll(
      ".bundle-colors__picker"
    );
    const swatchSource = this.indexSwatchesByValue(pickerElements);

    pickerElements.forEach((root) => {
      const select = this.matchingSelect(root, unclaimed);

      if (!select) {
        root.setAttribute("hidden", "hidden");
        return;
      }

      unclaimed.delete(select);

      this.applyDropdownTitle(root, select);
      this.adoptMissingSwatches(root, select, swatchSource);
      this.hideUnofferedSwatches(root, select);

      // The browser skips the disabled placeholder and lands on the first real
      // option, so the select reports a colour nobody picked. Reset both sides
      // to empty; only an actual pick fills them in.
      select.selectedIndex = -1;

      const hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = select.name;
      hiddenInput.value = "";
      this.productForm.appendChild(hiddenInput);

      this.pickers.push({ root, select, hiddenInput, swiper: this.swatchSwiper(root) });
    });

    this.resolveUnmatchedSelects(unclaimed);
  }

  /**
   * Decides what happens to colour groups no picker could be found for.
   *
   * The app marks the first sellable option of every group selected, so a group
   * left as found puts a colour on the order whether or not anyone chose it.
   * Hiding the app block on top of that is what makes it dangerous: the group
   * becomes invisible, unanswerable, and still bought. Emptying the select
   * stops that, and isComplete then holds Add to Cart until it is answered.
   *
   * Answering it needs somewhere to do so, and the theme has drawn no picker —
   * so the app's own selector stays on the page, with the groups that do have
   * pickers taken out of it. The page is the worse for it, which is the point:
   * this only happens when the bundle's components metafield and the app's
   * groups disagree, and it should be visible to whoever can fix that.
   *
   * @param {Set} unclaimed - the selects no picker matched.
   */
  resolveUnmatchedSelects(unclaimed) {
    this.orphanSelects = Array.from(unclaimed);

    // The Shopify block wrapper is the flex child of the PDP stack, so hiding
    // the inner element alone would leave its gap behind.
    const appBlock =
      this.appOptions.closest(".shopify-block") || this.appOptions;

    if (this.orphanSelects.length === 0) {
      appBlock.classList.add("bundle-colors__source--hidden");
      return;
    }

    this.orphanSelects.forEach((select) => (select.selectedIndex = -1));
    this.pickers.forEach((picker) => this.hideAppSelect(picker.select));
  }

  /**
   * Takes one group out of the app's selector, label and all, leaving the rest
   * of it on the page. The app renders them as siblings, so both are reachable
   * from the select's id.
   *
   * @param {HTMLSelectElement} select - the group a picker already draws.
   */
  hideAppSelect(select) {
    const label = this.appOptions.querySelector(`label[for="${select.id}"]`);

    select.classList.add("bundle-colors__source--hidden");

    if (label) label.classList.add("bundle-colors__source--hidden");
  }

  /**
   * Collects one piece of swatch markup for every colour the page draws.
   *
   * Every product in the components metafield gets a picker of its own, and a
   * picker always draws its own product first, so between them the pickers hold
   * a swatch for every colour in the bundle — including ones a given picker's
   * connected products metafield leaves out. This is what a picker adopts from
   * when the app offers a colour it did not render.
   *
   * @param {NodeList} pickerElements - every picker on the page.
   * @returns {Map} product title to the slide drawing it.
   */
  indexSwatchesByValue(pickerElements) {
    const byValue = new Map();

    pickerElements.forEach((root) => {
      root.querySelectorAll(".bundle-colors__swatch").forEach((swatch) => {
        if (byValue.has(swatch.dataset.value)) return;

        byValue.set(
          swatch.dataset.value,
          swatch.closest(".bundle-colors__swatch-slide")
        );
      });
    });

    return byValue;
  }

  /**
   * Finds the app select whose colour group this picker draws.
   *
   * Both sides name a colour by the product's title — the app in its option
   * values, the picker in its swatches' data-value — and that shared title is
   * the only identity they have in common. The group's own name cannot be used:
   * it lives on the app's label, which is why applyDropdownTitle reads it back
   * only once the pairing is made.
   *
   * Titles shared is not enough on its own. Where one group's colours are a
   * subset of another's, a picker drawing the smaller set matches both equally,
   * and counting overlap alone would let it claim the larger group and push the
   * larger picker onto a select that cannot fill it. Scoring the overlap against
   * everything either side offers settles that: a picker fits its own group
   * perfectly and any group containing it only partly.
   *
   * Pickers for the same group still score alike, and the first one asked wins
   * because the ones after it are offered a smaller pool.
   *
   * @param {HTMLElement} root - the picker to place.
   * @param {Set} candidates - the selects no picker has taken yet.
   * @returns {HTMLSelectElement|null} its select, or null if it shares no
   *   colour with any of them.
   */
  matchingSelect(root, candidates) {
    const values = new Set(
      Array.from(root.querySelectorAll(".bundle-colors__swatch")).map(
        (swatch) => swatch.dataset.value
      )
    );

    let best = null;
    let bestScore = 0;

    candidates.forEach((select) => {
      const offered = new Set(
        Array.from(select.options)
          .map((option) => option.value)
          .filter((value) => value !== "")
      );

      let shared = 0;
      offered.forEach((value) => values.has(value) && (shared += 1));

      if (shared === 0) return;

      const score = shared / (values.size + offered.size - shared);

      if (score > bestScore) {
        best = select;
        bestScore = score;
      }
    });

    return best;
  }

  /**
   * Draws the colours the app offers for this group that the picker is missing.
   *
   * A picker's swatches come from its product's connected products metafield,
   * which the merchant maintains by hand and which lags behind the app whenever
   * a colour is added to the bundle but not to that list. The app's select is
   * what actually sells, so any option it carries that the picker has no swatch
   * for is copied in from wherever the page already drew it. Copied rather than
   * moved, so the picker it came from is left as it was.
   *
   * @param {HTMLElement} root - the picker to fill in.
   * @param {HTMLSelectElement} select - the app select it is bound to.
   * @param {Map} swatchSource - product title to the slide drawing it.
   */
  adoptMissingSwatches(root, select, swatchSource) {
    const wrapper = root.querySelector(".swiper-wrapper");
    const drawn = new Set(
      Array.from(root.querySelectorAll(".bundle-colors__swatch")).map(
        (swatch) => swatch.dataset.value
      )
    );

    Array.from(select.options).forEach((option) => {
      if (option.value === "" || drawn.has(option.value)) return;

      const slide = swatchSource.get(option.value);

      if (!slide) return;

      wrapper.appendChild(slide.cloneNode(true));
    });
  }

  /**
   * Names the picker with the component's Dropdown title from the app.
   *
   * Liquid renders the component product's own title as a stand-in, because the
   * Dropdown title only exists inside the app block and cannot be reached at
   * render time. This is the value the merchant actually edits, so it replaces
   * the stand-in as soon as the picker is bound to its select.
   *
   * @param {HTMLElement} root - the picker element.
   * @param {HTMLSelectElement} select - the app select it is bound to.
   */
  applyDropdownTitle(root, select) {
    const label = this.appOptions.querySelector(`label[for="${select.id}"]`);
    const title = label
      ? label.textContent.trim()
      : select.name.replace(/^properties\[|\]$/g, "");

    if (!title) return;

    root.dataset.title = title;
    root.querySelector(".bundle-colors__name").textContent = title;
  }

  /**
   * Attaches to the swatch slider for one picker.
   *
   * ProductElement initialises every '.swiper' inside '.pm__information' from its
   * data-swiper-settings and runs first, so adopt that instance rather than
   * starting a second one on the same track. After a soft product swap it does
   * not run again and this builds its own.
   *
   * @param {HTMLElement} root - the picker element.
   * @returns {Object|null} the Swiper instance driving its swatches.
   */
  swatchSwiper(root) {
    const track = root.querySelector(".bundle-colors__swatch-track");

    if (!track) return null;

    const swiper = track.swiper || new RevolutionSwiper(track).swiperElement;
    const slider = root.querySelector(".bundle-colors__swatch-slider");
    const updateOverlays = () => {
      slider.setAttribute("data-can-scroll-left", !swiper.isBeginning);
      slider.setAttribute("data-can-scroll-right", !swiper.isEnd);
    };

    swiper.on("progress", updateOverlays);
    swiper.on("resize", updateOverlays);

    return swiper;
  }

  /**
   * Removes swatches the app is not offering for this component, and marks the
   * ones this market cannot sell as sold out.
   *
   * Either signal can take a colour off sale, and both are asked. The app's
   * disabled flag is its global inventory total, which reads the same in every
   * market and so cannot see a warehouse running dry; the stock Liquid put on
   * the swatch is this market's number, written by a Flow that can lag or fail.
   * Taking the metafield as the whole answer would leave a colour the app has
   * already stopped selling selectable on a stale positive number.
   *
   * @param {HTMLElement} root - the picker element.
   * @param {HTMLSelectElement} select - the app select it is bound to.
   */
  hideUnofferedSwatches(root, select) {
    const options = new Map(
      Array.from(select.options)
        .filter((option) => option.value !== "")
        .map((option) => [option.value, option])
    );

    root.querySelectorAll(".bundle-colors__swatch").forEach((swatch) => {
      const option = options.get(swatch.dataset.value);

      if (!option) {
        swatch.closest(".bundle-colors__swatch-slide").remove();
        return;
      }

      const outOfStockInMarket =
        swatch.dataset.marketStockEnforced === "true" &&
        Number(swatch.dataset.marketStock) < 1;

      if (option.disabled || outOfStockInMarket) {
        swatch.classList.add("bundle-colors__swatch--sold-out");
        swatch.setAttribute("aria-disabled", "true");
      }
    });
  }

  /**
   * Binds every event this element listens to.
   *
   * The toggles are native <button>s, which already turn Enter into a click, so
   * a keypress listener would fire each of these twice per keyboard press.
   */
  bindEvents() {
    this.delegate.on(
      "click",
      '[data-action="toggle-bundle-colors"]',
      this.toggleStep.bind(this)
    );
    this.delegate.on(
      "click",
      '[data-action="toggle-color-picker"]',
      this.togglePicker.bind(this)
    );
    this.delegate.on(
      "click",
      '[data-action="pick-color"]',
      this.pickColor.bind(this)
    );

    // Capture phase, so this runs before AddToCart's own delegated handler and
    // can stop an incomplete bundle from ever reaching /cart/add.js.
    this.addToCartGuard = this.guardAddToCart.bind(this);
    document.body.addEventListener("click", this.addToCartGuard, true);
  }

  /**
   * Blocks Add to Cart while any component is still without a colour, and sends
   * the customer to the first one that needs answering.
   *
   * @param {Event} event - the captured click.
   */
  guardAddToCart(event) {
    const button = event.target.closest(
      '[data-action="add-to-cart"], [data-action="add-to-cart-og"]'
    );

    if (!button) return;

    // A bundle PDP carries several add-to-cart buttons — the bundle's own plus
    // one on every upsell and recommendation card. Only the bundle's is gated;
    // blocking the rest would stop a customer buying anything else on the page.
    const isBundleButton = button.closest(BUY_BUTTONS_SELECTOR);

    if (!isBundleButton || this.isComplete()) return;

    event.preventDefault();
    event.stopPropagation();

    this.askForMissingColor(button);
  }

  /**
   * Opens the first unanswered picker, scrolls to it, and says so on the button.
   *
   * @param {HTMLElement} button - the Add to Cart button that was pressed.
   */
  askForMissingColor(button) {
    const flagMissing = true;
    const target =
      this.openFirstUnpicked(flagMissing) || this.unansweredSelect();

    if (target) this.scrollToPicker(target);

    this.showMissingLabel(button);
  }

  /**
   * Swaps the button's label to the prompt, then puts the original back.
   *
   * @param {HTMLElement} button - the Add to Cart button to relabel.
   */
  showMissingLabel(button) {
    const label = button.querySelector(".ButtonTextStart");

    if (!label) return;

    clearTimeout(this.missingLabelTimeout);

    if (this.originalButtonLabel === undefined) {
      this.originalButtonLabel = label.textContent;
    }

    label.textContent = this.element.dataset.missingText;

    this.missingLabelTimeout = setTimeout(() => {
      label.textContent = this.originalButtonLabel;
      this.originalButtonLabel = undefined;
    }, MISSING_LABEL_DURATION);
  }

  /**
   * Opens or closes the whole step.
   */
  toggleStep() {
    const isOpen = this.stepToggle.getAttribute("aria-expanded") === "true";

    this.stepToggle.setAttribute("aria-expanded", !isOpen);
    this.stepContent.setAttribute("aria-hidden", isOpen);
  }

  /**
   * Opens one picker and closes the rest — the design allows one at a time.
   *
   * @param {Event} event - the click event.
   * @param {HTMLElement} target - the picker's toggle button.
   */
  togglePicker(event, target) {
    const pickerRoot = target.closest(".bundle-colors__picker");
    const wasOpen = target.getAttribute("aria-expanded") === "true";

    this.closeAllPickers();

    if (!wasOpen) this.setPickerOpen(pickerRoot, true);
  }

  closeAllPickers() {
    this.pickers.forEach((picker) => this.setPickerOpen(picker.root, false));
  }

  /**
   * @param {HTMLElement} pickerRoot - the picker to change.
   * @param {boolean} isOpen - true to open it.
   */
  setPickerOpen(pickerRoot, isOpen) {
    pickerRoot
      .querySelector(".bundle-colors__toggle")
      .setAttribute("aria-expanded", isOpen);
    pickerRoot
      .querySelector(".bundle-colors__swatches")
      .setAttribute("aria-hidden", !isOpen);

    if (!isOpen) return;

    // The rows start collapsed, so the slider was measured at zero width when
    // the page loaded. It has to re-measure the first time it is shown.
    const picker = this.pickers.find((item) => item.root === pickerRoot);

    if (picker && picker.swiper) {
      picker.swiper.update();
      this.updateSwatchOverlays(picker);
    }
  }

  /**
   * Shows the edge fade only on a side that still has swatches to scroll to.
   *
   * @param {Object} picker - the picker whose slider to read.
   */
  updateSwatchOverlays(picker) {
    const slider = picker.root.querySelector(".bundle-colors__swatch-slider");

    slider.setAttribute("data-can-scroll-left", !picker.swiper.isBeginning);
    slider.setAttribute("data-can-scroll-right", !picker.swiper.isEnd);
  }

  /**
   * Records a colour choice: the app's select so it can reprice, the hidden
   * input so the choice reaches the cart, and the row's labels so the customer
   * can see it. The next picker is deliberately not opened — the design leaves
   * that to the customer.
   *
   * @param {Event} event - the click event.
   * @param {HTMLElement} target - the swatch that was pressed.
   */
  pickColor(event, target) {
    if (target.getAttribute("aria-disabled") === "true") return;

    const pickerRoot = target.closest(".bundle-colors__picker");
    const picker = this.pickers.find((item) => item.root === pickerRoot);
    const value = target.dataset.value;

    picker.select.value = value;
    picker.select.dispatchEvent(new Event("change", { bubbles: true }));
    picker.hiddenInput.value = value;

    // The colour text is rendered twice — once on the name's line for the closed
    // row, once beneath it for the open row — so both are kept in step.
    pickerRoot.querySelectorAll('[data-role="value"]').forEach((label) => {
      label.textContent = target.dataset.name;
    });

    pickerRoot.classList.add("bundle-colors__picker--picked");
    pickerRoot.classList.remove("bundle-colors__picker--missing");

    pickerRoot
      .querySelectorAll(".bundle-colors__swatch")
      .forEach((swatch) =>
        swatch.classList.remove("bundle-colors__swatch--selected")
      );
    target.classList.add("bundle-colors__swatch--selected");

    this.announceSelections();
  }

  /**
   * Publishes the current state for step 3 to follow.
   */
  announceSelections() {
    this.element.dispatchEvent(
      new CustomEvent("bundle:colors-changed", {
        bubbles: true,
        detail: { selections: this.selections(), complete: this.isComplete() },
      })
    );
  }

  /**
   * @returns {Array} one entry per component: where it sits, what was chosen,
   *   and the name and image of that choice.
   */
  selections() {
    return this.pickers.map((picker) => {
      const selected = picker.root.querySelector(
        ".bundle-colors__swatch--selected"
      );

      return {
        index: picker.root.dataset.pickerIndex,
        title: picker.root.dataset.title || "",
        value: picker.hiddenInput.value,
        name: selected ? selected.dataset.name : "",
        image: selected ? selected.dataset.image : "",
      };
    });
  }

  /**
   * @returns {boolean} true once every component has a colour, including any
   *   group left to the app's own selector.
   */
  isComplete() {
    return (
      this.pickers.every((picker) => picker.hiddenInput.value !== "") &&
      this.orphanSelects.every((select) => select.value !== "")
    );
  }

  /**
   * @returns {HTMLSelectElement|undefined} the first group without a picker
   *   still waiting for a colour.
   */
  unansweredSelect() {
    return this.orphanSelects.find((select) => select.value === "");
  }

  /**
   * Opens one component's picker and brings it into view. Step 3 calls this when
   * a customer taps a row that has no colour yet.
   *
   * @param {string} index - the picker's position, as a string from the dataset.
   * @returns {HTMLElement|null} the picker that was opened.
   */
  openPicker(index) {
    const picker = this.pickers.find(
      (item) => item.root.dataset.pickerIndex === index
    );

    if (!picker) return null;

    this.closeAllPickers();
    this.setPickerOpen(picker.root, true);
    this.scrollToPicker(picker.root);

    return picker.root;
  }

  /**
   * @param {HTMLElement} pickerRoot - the picker to bring into view, or the app
   *   select standing in for a group that has none.
   */
  scrollToPicker(pickerRoot) {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    pickerRoot.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "center",
    });
  }

  /**
   * Opens the first picker still without a colour, which is both the load state
   * and where Add to Cart sends the customer when something is missing.
   *
   * @param {boolean} flagMissing - also mark the row so it reads as required.
   * @returns {HTMLElement|null} the picker that was opened.
   */
  openFirstUnpicked(flagMissing) {
    const picker = this.pickers.find((item) => item.hiddenInput.value === "");

    if (!picker) return null;

    this.closeAllPickers();
    this.setPickerOpen(picker.root, true);

    if (flagMissing) {
      picker.root.classList.add("bundle-colors__picker--missing");
    }

    return picker.root;
  }
}

customElements.define("bundle-colors", BundleColors);

/**
 * Bundle PDP — step 3, "What is included in the bundle".
 *
 * A read-only view of the step 2 choices. The rows are rendered in Liquid; this
 * only fills in the image and colour name as pickers are answered. Tapping a row
 * that has no colour yet sends the customer back to the picker that needs it.
 */
class BundleSummary extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.delegate = new Delegate(this.element);
  }

  connectedCallback() {
    this.toggleButton = this.element.querySelector(
      '[data-action="toggle-bundle-summary"]'
    );
    this.content = this.element.querySelector(".bundle-summary__content");
    this.colorPickers = document.querySelector("bundle-colors");

    // Nothing to summarise without step 2.
    if (!this.colorPickers) {
      this.showEditorNotice();
      return;
    }

    this.bindEvents();

    // bundle-colors is defined first, so its opening announcement has already
    // been and gone by the time this element exists. Read the state directly.
    this.render(this.colorPickers.selections());
  }

  disconnectedCallback() {
    this.delegate.destroy();
    document.removeEventListener("bundle:colors-changed", this.changeListener);
  }

  /**
   * A step that cannot render leaves nothing behind on the storefront. In the
   * theme editor it says what it is waiting for instead, since a step that
   * silently vanishes there reads as a broken block.
   */
  showEditorNotice() {
    const notice = this.element.querySelector('[data-role="editor-notice"]');

    if (!notice) {
      this.element.setAttribute("hidden", "hidden");
      return;
    }

    notice.hidden = false;
    this.element
      .querySelectorAll('[data-role="step"]')
      .forEach((part) => (part.hidden = true));
  }


  bindEvents() {
    this.delegate.on(
      "click",
      '[data-action="toggle-bundle-summary"]',
      this.toggleContent.bind(this)
    );
    this.delegate.on(
      "click",
      '[data-action="edit-bundle-color"]',
      this.editColor.bind(this)
    );

    this.changeListener = this.handleColorsChanged.bind(this);
    document.addEventListener("bundle:colors-changed", this.changeListener);
  }

  /**
   * Opens or closes the summary. Closed on load, as the design asks.
   */
  toggleContent() {
    const isOpen = this.toggleButton.getAttribute("aria-expanded") === "true";

    this.toggleButton.setAttribute("aria-expanded", !isOpen);
    this.content.setAttribute("aria-hidden", isOpen);
  }

  /**
   * @param {CustomEvent} event - carries the selections from step 2.
   */
  handleColorsChanged(event) {
    this.render(event.detail.selections);
  }

  /**
   * Fills each row in with the colour chosen for it, matching rows to pickers by
   * position. Rows whose picker has no answer are left as rendered.
   *
   * @param {Array} selections - one entry per component still in play.
   */
  render(selections) {
    const live = new Set(selections.map((one) => one.index));

    // A row whose picker the app does not offer can never be answered, so it is
    // taken off the summary rather than left showing an unfillable "+".
    this.element.querySelectorAll("[data-row-index]").forEach((row) => {
      row.hidden = !live.has(row.dataset.rowIndex);
    });

    selections.forEach((selection) => {
      const row = this.element.querySelector(
        `[data-row-index="${selection.index}"]`
      );

      if (!row) return;

      // The summary names each row with the same Dropdown title step 2 uses.
      if (selection.title) {
        row.querySelector(".bundle-summary__row-name").textContent =
          selection.title;
      }

      if (!selection.value) return;

      row.classList.remove("bundle-summary__row--empty");
      row.querySelector('[data-role="value"]').textContent = selection.name;

      const image = row.querySelector('[data-role="image"]');
      const placeholder = row.querySelector('[data-role="placeholder"]');

      // The "+" means "not chosen yet", so it goes as soon as one is, whether or
      // not there is a picture to put in its place.
      placeholder.hidden = true;

      if (selection.image) {
        image.src = selection.image;
        image.hidden = false;
      }
    });

  }

  /**
   * Sends the customer back to the picker for the row they pressed.
   *
   * @param {Event} event - the click event.
   * @param {HTMLElement} target - the row that was pressed.
   */
  editColor(event, target) {
    this.colorPickers.openPicker(target.dataset.rowIndex);
  }
}

customElements.define("bundle-summary", BundleSummary);
